import React, { useState } from 'react'
import { Form, InputNumber, Select, Switch, Table, Tabs } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import { Modal as SgModal } from '@/components/dialog/modal'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import {
    createPointsCouponItem,
    getPointsCouponCandidates,
    getPointsCouponItems,
    getPointsCouponMallConfig,
    getPointsCouponRecords,
    updatePointsCouponItem,
    updatePointsCouponMallConfig,
} from '../api'
import SettingPage, { SettingPanel, SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

interface ItemForm {
    coupon_id: number
    required_points: number
    enabled: boolean
}

const PointsCouponMall: React.FC = () => {
    const [form] = Form.useForm<ItemForm>()
    const [tab, setTab] = useState('items')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [visible, setVisible] = useState(false)
    const [current, setCurrent] = useState<any>(null)
    const configReq = useRequest(getPointsCouponMallConfig, {
        onError: e => $msg('error', e.message || '获取积分商城设置失败'),
    })
    const itemReq = useRequest(getPointsCouponItems, {
        onError: e => $msg('error', e.message || '获取兑换商品失败'),
    })
    const couponReq = useRequest(getPointsCouponCandidates, {
        onError: e => $msg('error', e.message || '获取可选优惠券失败'),
    })
    const recordReq = useRequest(() => getPointsCouponRecords({ page, limit: pageSize }), {
        ready: tab === 'records',
        refreshDeps: [tab, page, pageSize],
        onError: e => $msg('error', e.message || '获取兑换记录失败'),
    })
    const configSaveReq = useRequest(updatePointsCouponMallConfig, {
        manual: true,
        onSuccess: () => {
            $msg('success', '设置成功')
            configReq.refresh()
        },
        onError: e => $msg('error', e.message || '保存积分商城设置失败'),
    })
    const itemSaveReq = useRequest(
        async (values: ItemForm) => {
            if (current) {
                await updatePointsCouponItem({ item_id: current.id, ...values })
            } else {
                await createPointsCouponItem(values)
            }
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', current ? '编辑成功' : '添加成功')
                setVisible(false)
                itemReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存兑换商品失败'),
        },
    )
    const openEditor = (item?: any) => {
        setCurrent(item || null)
        form.setFieldsValue({
            coupon_id: item?.coupon?.id,
            required_points: Number(item?.required_points || 1),
            enabled: item ? !!item.enabled : true,
        })
        setVisible(true)
    }
    const itemColumns: ColumnsType<any> = [
        { title: '优惠券', dataIndex: ['coupon', 'name'], render: value => value || '--' },
        { title: '优惠金额', dataIndex: ['coupon', 'amount'], width: 130, render: value => `${value || 0} 元` },
        { title: '所需积分', dataIndex: 'required_points', width: 130, render: value => value || 0 },
        {
            title: '剩余数量',
            dataIndex: 'coupon',
            width: 130,
            render: value => value?.quantity ?? '--',
        },
        {
            title: '状态',
            dataIndex: 'enabled',
            width: 120,
            render: (value, record) => (
                <Auth
                    perssion={35}
                    onClick={() =>
                        updatePointsCouponItem({ item_id: record.id, enabled: !value })
                            .then(() => {
                                $msg('success', '设置成功')
                                itemReq.refresh()
                            })
                            .catch(e => $msg('error', e.message || '设置失败'))
                    }
                >
                    <Switch checked={!!value} checkedChildren="启用" unCheckedChildren="停用" />
                </Auth>
            ),
        },
        {
            title: '操作',
            width: 100,
            render: (_value, record) => (
                <Auth perssion={35} onClick={() => openEditor(record)}>
                    <span className="action">编辑</span>
                </Auth>
            ),
        },
    ]
    const recordColumns: ColumnsType<any> = [
        {
            title: '兑换时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        {
            title: '会员',
            dataIndex: 'membership',
            width: 170,
            render: value => value?.name || value?.user?.nickname || '--',
        },
        { title: '手机号', dataIndex: ['membership', 'phone'], width: 150, render: value => value || '--' },
        {
            title: '优惠券',
            width: 180,
            render: (_value, record) =>
                record.member_coupon?.coupon?.name || record.exchange_item?.coupon?.name || '--',
        },
        { title: '消耗积分', dataIndex: 'points_cost', width: 120, render: value => value || 0 },
        {
            title: '结果',
            dataIndex: 'status',
            width: 120,
            render: (value, record) => (value === 1 ? '成功' : record.failure_message || '失败'),
        },
    ]

    return (
        <SettingPage
            title="积分商城"
            extra={
                tab === 'items' ? (
                    <Auth perssion={35} onClick={() => openEditor()}>
                        <Button types="default">添加兑换券</Button>
                    </Auth>
                ) : (
                    <Button types="primary" loading={recordReq.loading} onClick={() => recordReq.refresh()}>
                        刷新
                    </Button>
                )
            }
            append={
                visible ? (
                    <SgModal
                        title={current ? '编辑兑换券' : '添加兑换券'}
                        isShow
                        type="success"
                        loading={itemSaveReq.loading}
                        okText="保存"
                        cancelText="取消"
                        onClose={() => setVisible(false)}
                        onCancel={() => setVisible(false)}
                        onConfirm={() => form.validateFields().then(values => itemSaveReq.run(values))}
                    >
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="coupon_id"
                                label="优惠券"
                                rules={[{ required: true, message: '请选择优惠券' }]}
                            >
                                <Select
                                    disabled={!!current}
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="请选择优惠券"
                                >
                                    {(couponReq.data?.coupons || []).map((coupon: any) => (
                                        <Select.Option key={coupon.id} value={coupon.id}>
                                            {coupon.name}（{coupon.amount || 0}元）
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="required_points"
                                label="兑换所需积分"
                                rules={[{ required: true, message: '请输入积分' }]}
                            >
                                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="enabled" label="状态" valuePropName="checked">
                                <Switch checkedChildren="启用" unCheckedChildren="停用" />
                            </Form.Item>
                        </Form>
                    </SgModal>
                ) : null
            }
        >
            <SettingPanel
                title="积分商城状态"
                desc="开启后，顾客可以在甜杏小程序使用积分兑换已启用的优惠券。"
                extra={
                    <Auth
                        perssion={35}
                        onClick={() => configSaveReq.run({ enabled: !configReq.data?.config?.enabled })}
                    >
                        <Switch
                            checked={!!configReq.data?.config?.enabled}
                            loading={configReq.loading || configSaveReq.loading}
                        />
                    </Auth>
                }
                wide
            >
                <div className="member-config-page__notice">
                    积分商城目前仅支持独立门店；连锁总部与分店暂不支持配置。
                </div>
            </SettingPanel>
            <Tabs
                activeKey={tab}
                onChange={value => {
                    setTab(value)
                    setPage(1)
                }}
            >
                <Tabs.TabPane tab="兑换券配置" key="items" />
                <Tabs.TabPane tab="兑换记录" key="records" />
            </Tabs>
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey="id"
                    loading={tab === 'items' ? itemReq.loading : recordReq.loading}
                    columns={tab === 'items' ? itemColumns : recordColumns}
                    dataSource={tab === 'items' ? itemReq.data?.items || [] : recordReq.data?.records || []}
                    pagination={
                        tab === 'items'
                            ? false
                            : getMemberTablePagination({
                                  current: page,
                                  pageSize,
                                  total: getTableTotal(recordReq.data?.pagination),
                                  onChange: setPage,
                                  onShowSizeChange: (nextPage, nextSize) => {
                                      setPage(nextPage)
                                      setPageSize(nextSize)
                                  },
                              })
                    }
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default PointsCouponMall
